import { API, CortexCustomizationGroup, CortexCustomizationReport, CustomizationSuggestion, PlacementApplyResult } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { classifyLegacyFlag } from "@swivl-mono/types";
import { Button, ButtonVariant, Spinner } from "@swivl-mono/ui";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { FaLocationDot } from "react-icons/fa6";
import { FiChevronDown } from "react-icons/fi";

import { Badge } from "../../DocumentIngest/Badge";
import { ScanIncompleteNotice } from "./ScanIncompleteNotice";
import { RunSaveState, UnmigratedRunCallout } from "./UnmigratedRunCallout";
import { MigrationStepHandle } from "./workflow";

interface CustomizedUnmappedTabProps {
  orgId: number;
  /** Called after this page writes its answers into Policies, so the workflow can refresh steps that
   * read what it wrote. */
  onApplied: () => void;
  /** Called after the run is saved, so the overview's last-saved time is current. */
  onRunSaved: () => void;
}

/** Group the not-carrying-over items into scannable blocks by key family. Order = priority. */
const CATEGORIES: { label: string; test: (key: string) => boolean }[] = [
  { label: "Twilio", test: (k) => k.startsWith("Twilio") },
  { label: "FMS", test: (k) => k.includes("FMS") },
  { label: "Sequences", test: (k) => /sequence|abandonedcart/i.test(k) },
  { label: "Voice", test: (k) => /voice/i.test(k) },
  { label: "CTA / Spot", test: (k) => k.startsWith("Spot") },
  { label: "Integrations", test: (k) => k.includes("Integration") },
  { label: "Profile settings", test: (k) => k.startsWith("ProfileSetting") },
  { label: "Links & URLs", test: (k) => k.includes("URL") }
];
const OTHER = "Other";

function categorize(key: string): string {
  return CATEGORIES.find((c) => c.test(key))?.label ?? OTHER;
}

interface CategorySection {
  label: string;
  groups: CortexCustomizationGroup[];
}

function groupByCategory(groups: CortexCustomizationGroup[]): CategorySection[] {
  const grouped = new Map<string, CortexCustomizationGroup[]>();
  for (const g of groups) {
    const cat = categorize(g.key);
    const arr = grouped.get(cat) ?? [];
    arr.push(g);
    grouped.set(cat, arr);
  }
  return [...CATEGORIES.map((c) => c.label), OTHER].filter((c) => grouped.has(c)).map((c) => ({ label: c, groups: grouped.get(c)! }));
}

const EMPTY_REPORT: CortexCustomizationReport = { carryingOver: [], notCarryingOver: [], scanIncomplete: false };

/** Client-side only, same Blob + `<a download>` pattern as Billing.View.tsx's location export — no
 * new server endpoint needed since the report + suggestions are already fetched. One CSV row per
 * (key, scope) — a group with 3 locations becomes 3 rows, each carrying the group's own suggestion. */
function downloadCsv(report: CortexCustomizationReport, suggestions: Map<string, CustomizationSuggestion>, orgId: number): void {
  const escape = (v: string): string => `"${v.replace(/"/g, '""')}"`;
  const headers = ["Carries over", "Kind", "Key", "Label", "Scope", "Value", "Destination / reason", "Suggested policy", "Suggested note"];
  const rows: string[] = [headers.join(",")];
  const addGroup = (group: CortexCustomizationGroup): void => {
    for (const loc of group.locations) {
      const sug = suggestions.get(suggestionKey(group.key, loc.value ?? ""));
      rows.push(
        [
          loc.destination ? "Yes" : "No",
          group.kind,
          group.key,
          group.label,
          loc.scopeLabel,
          loc.value,
          loc.destination ?? loc.reason ?? "",
          sug?.suggestedPolicyTitle ?? "",
          sug?.suggestedSentence ?? ""
        ]
          .map(escape)
          .join(",")
      );
    }
  };
  for (const g of report.carryingOver) addGroup(g);
  for (const g of report.notCarryingOver) addGroup(g);

  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `org-${orgId}-unmigrated-content.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// `divide-x` gives every column a real vertical rule instead of relying on `gap` alone.
const DETAIL_GRID = "grid grid-cols-[minmax(140px,1fr)_minmax(0,1.4fr)_minmax(0,1.6fr)] items-center gap-3 divide-x divide-grey-100";

/** Header + per-location breakdown table shared by every row shape in this file — Location / Value /
 * Status, "Carries over" in teal, a failure reason otherwise. `formatValue` lets a caller apply its
 * own display transform (e.g. the toggle rows' "1"/"0" -> "On"/"Off") without duplicating this table. */
function LocationBreakdown({
  group,
  formatValue = (v) => v
}: {
  group: CortexCustomizationGroup;
  formatValue?: (value: string) => string;
}): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-[4px] border border-grey-200 bg-white">
      <div className={filterClasses(DETAIL_GRID, "border-b border-grey-200 px-3 py-1.5")}>
        <span className="text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase">Location</span>
        <span className="text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase">Value</span>
        <span className="text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase">Status</span>
      </div>
      {group.locations.map((loc) => (
        <div key={loc.scope} className={filterClasses(DETAIL_GRID, "border-b border-grey-100 px-3 py-2 last:border-b-0")}>
          <span className="truncate text-[13px] text-grey-700">{loc.scopeLabel}</span>
          <span className="text-[13px] [overflow-wrap:anywhere] text-grey-500">{formatValue(loc.value)}</span>
          {loc.destination ? (
            <span className="text-[12px] text-teal-700">Carries over</span>
          ) : (
            <span className="text-[12px] [overflow-wrap:anywhere] text-grey-500">{loc.reason}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/** The team's triage call for one free-text field with no Cortex V3 destination: "implement" = we
 * already have a good suggestion for how to resolve it; "true-gap" = no Snowy field (Details or
 * Policy Builder) could capture this at all; "needs-review" = unclear, worth a closer look.
 * "implement" is a live signal (a suggestion exists); the other two are the model's own judgment,
 * since there's no other way to tell "definitely no home" from "genuinely unsure" apart. */
type Outcome = "implement" | "catalog-gap" | "already-covered" | "true-gap" | "needs-review";

const OUTCOME_BADGE: Record<Outcome, { label: string; className: string }> = {
  implement: { label: "Answer a policy question", className: "bg-teal-50 text-teal-700" },
  "catalog-gap": { label: "Missing policy question", className: "bg-purple-50 text-purple-700" },
  "already-covered": { label: "Already covered", className: "bg-grey-100 text-grey-600" },
  "true-gap": { label: "True Gap", className: "bg-red-100 text-red-700" },
  "needs-review": { label: "Needs Review", className: "bg-yellow-50 text-yellow-700" }
};

/** Undefined = the suggestion hasn't loaded yet — never guess before the classification is known. */
function outcomeFor(suggestion?: CustomizationSuggestion): Outcome | undefined {
  if (!suggestion) return undefined;
  if (suggestion.alreadyCovered) return "already-covered";
  if (suggestion.suggestedItemKey) return "implement";
  if (suggestion.catalogGap) return "catalog-gap";
  return suggestion.noPlausibleDestination ? "true-gap" : "needs-review";
}

/** A suggestion eligible for the pre-ticked "apply this answer" path: an actual placement the model
 * itself rated high. Everything else stays advisory — see `SuggestionConfidence`. */
function isAutoApplicable(suggestion?: CustomizationSuggestion): boolean {
  return (
    suggestion?.confidence === "high" &&
    Boolean(suggestion.suggestedPolicyKey) &&
    Boolean(suggestion.suggestedItemKey) &&
    Boolean(suggestion.suggestedSentence)
  );
}

const VALUE_TRUNCATE_LENGTH = 140;

// `divide-x` gives every column a real vertical rule instead of relying on `gap` alone. White card,
// not red — the status badges per row already carry the "needs a look" signal; a full red fill on top
// of that was an eyesore, not an additional signal.
const FREE_TEXT_ROW_GRID = "grid grid-cols-[minmax(180px,1fr)_minmax(0,1.2fr)_minmax(0,1.3fr)] items-start gap-4 divide-x divide-grey-200";
const FREE_TEXT_HEADER_LABEL = "text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase";

// `divide-x` gives every column a real vertical rule instead of relying on `gap` alone.
const CUSTOMIZATION_ROW_GRID = "grid grid-cols-[minmax(180px,1fr)_minmax(0,1.6fr)] items-start gap-4 divide-x divide-grey-200";
const CUSTOMIZATION_HEADER_LABEL = "text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase";

/** "1"/"true" -> "On", "0"/"false" -> "Off" — a raw boolean flag reads a lot faster this way than the
 * literal legacy string. Anything else (a value that isn't a clean boolean, e.g. real free text) passes
 * through unchanged, so this is safe to use for every section, not just genuine toggles. */
function displayToggleValue(value: string): string {
  const flag = classifyLegacyFlag(value);
  return flag === "on" ? "On" : flag === "off" ? "Off" : value;
}

interface CustomizationRowProps {
  group: CortexCustomizationGroup;
}

/** One field, one row, in the Legacy field / Value & reason shape — used everywhere in this tab that
 * doesn't need the Suggested-outcome column (free text keeps its own `FreeTextOutcomeRow`, which has a
 * suggestion to show): "Bot-handled — no action needed," "Already covered," "What's being migrated,"
 * "Toggles & settings not carrying over," and "Dropped on purpose." A carrying item's `destination` shows in teal; a
 * non-carrying item's `reason` shows in grey. Same multi-location expand chip + breakdown
 * (`LocationBreakdown`) as `FreeTextOutcomeRow`. */
function CustomizationRow({ group }: CustomizationRowProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const multiLocation = group.locations.length > 1;
  const value = multiLocation ? "Varies by location" : displayToggleValue(group.locations[0]?.value ?? "");

  return (
    <div className="border-b border-grey-100 last:border-b-0">
      <div className={filterClasses(CUSTOMIZATION_ROW_GRID, "px-4 py-3")}>
        {/* Legacy field */}
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="truncate text-[13px] font-semibold text-grey-900">{group.label}</span>
            {multiLocation && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className={filterClasses(
                  "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[12px] font-bold whitespace-nowrap transition-colors",
                  expanded ? "border-grey-900 bg-grey-900 text-white" : "border-orange-100 bg-orange-50 text-orange-700 hover:bg-orange-100"
                )}
              >
                <FaLocationDot className="size-3" />
                {group.locations.length} locations
                <FiChevronDown className={filterClasses("size-3 transition-transform", expanded && "rotate-180")} />
              </button>
            )}
          </div>
          {!multiLocation && group.locations[0] && <span className="text-[12px] text-grey-500">{group.locations[0].scopeLabel}</span>}
        </div>

        {/* Value & reason */}
        <div className="min-w-0">
          <span className="text-[13px] [overflow-wrap:anywhere] text-grey-700">{value}</span>
          {group.destination && <p className="mt-1 text-[12px] [overflow-wrap:anywhere] text-teal-700">Carries over: {group.destination}</p>}
          {group.reason && <p className="mt-1 text-[12px] [overflow-wrap:anywhere] text-grey-400">{group.reason}</p>}
        </div>
      </div>

      {expanded && multiLocation && (
        <div className="px-4 pb-3">
          <LocationBreakdown group={group} formatValue={displayToggleValue} />
        </div>
      )}
    </div>
  );
}

/** One distinct value of a field, plus every scope that holds it. A field set identically at 86
 * locations is ONE variant — the old row keyed "varies by location" off the location count alone, so
 * an unvarying field displayed no value at all while its outcome came from a value never shown. */
interface ValueVariant {
  value: string;
  scopeLabels: string[];
}

/** Mirrors the server's MAX_VARIANTS_PER_KEY. A field like a per-location phone number has a
 * distinct value at every location; reviewing 86 of them one by one is noise, so the most common few
 * are shown and the rest are summarised. Keep in step with CortexPolicyMigration.Service. */
const MAX_SHOWN_VARIANTS = 5;

function variantsOf(group: CortexCustomizationGroup): ValueVariant[] {
  const byValue = new Map<string, ValueVariant>();
  for (const loc of group.locations) {
    const value = (loc.value ?? "").trim();
    if (value === "") continue;
    const bucket = byValue.get(value) ?? { value, scopeLabels: [] };
    bucket.scopeLabels.push(loc.scopeLabel);
    byValue.set(value, bucket);
  }
  return [...byValue.values()].sort((a, b) => b.scopeLabels.length - a.scopeLabels.length);
}

/** Suggestions are one per (key, exact value), so the outcome always refers to the text beside it. */
function suggestionKey(key: string, value: string): string {
  return `${key}\u0000${value.trim()}`;
}

interface FreeTextOutcomeRowProps {
  group: CortexCustomizationGroup;
  variant: ValueVariant;
  /** >1 when this field holds different text at different scopes — each gets its own row. */
  variantCount: number;
  suggestion?: CustomizationSuggestion;
  suggestionsLoading: boolean;
  /** Only ever true for a high-confidence placement — see `isAutoApplicable`. */
  selectable: boolean;
  selected: boolean;
  onToggleSelected: () => void;
}

/** One free-text field, one row, in the Legacy field / Exact value / Suggested outcome shape. The
 * per-location breakdown is the shared `LocationBreakdown` (same as `CustomizationRow`) — it just
 * lives under the first column instead of the whole row, since this row has two more columns of its
 * own (the outcome badge column is why free text doesn't just use `CustomizationRow` directly). */
function FreeTextOutcomeRow({
  group,
  variant,
  variantCount,
  suggestion,
  suggestionsLoading,
  selectable,
  selected,
  onToggleSelected
}: FreeTextOutcomeRowProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [valueExpanded, setValueExpanded] = useState(false);
  const multiLocation = variant.scopeLabels.length > 1;
  // Always the real text this suggestion was judged against — never a "varies by location" placeholder.
  const value = variant.value;
  const isLongValue = value.length > VALUE_TRUNCATE_LENGTH;
  const displayValue = isLongValue && !valueExpanded ? `${value.slice(0, VALUE_TRUNCATE_LENGTH)}…` : value;
  const outcome = outcomeFor(suggestion);

  return (
    <div className="border-b border-grey-200 last:border-b-0">
      <div className={filterClasses(FREE_TEXT_ROW_GRID, "px-5 py-3.5")}>
        {/* Legacy field */}
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold text-grey-900">{group.label}</span>
            {multiLocation && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className={filterClasses(
                  "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[12px] font-bold whitespace-nowrap transition-colors",
                  expanded ? "border-grey-900 bg-grey-900 text-white" : "border-orange-100 bg-orange-50 text-orange-700 hover:bg-orange-100"
                )}
              >
                <FaLocationDot className="size-3" />
                {variant.scopeLabels.length} locations
                <FiChevronDown className={filterClasses("size-3 transition-transform", expanded && "rotate-180")} />
              </button>
            )}
          </div>
          {!multiLocation && <span className="text-[12px] text-grey-500">{variant.scopeLabels[0]}</span>}
          {variantCount > 1 && <span className="text-[12px] text-grey-400">one of {variantCount} different values for this field</span>}
        </div>

        {/* Exact value */}
        <div className="min-w-0">
          <p className="text-[13px] [overflow-wrap:anywhere] text-grey-700">{displayValue}</p>
          {isLongValue && (
            <button
              type="button"
              onClick={() => setValueExpanded((v) => !v)}
              className="mt-1 text-[12px] font-semibold text-orange-600 hover:underline"
            >
              {valueExpanded ? "Show less" : "Show full value"}
            </button>
          )}
        </div>

        {/* Suggested outcome */}
        <div className="min-w-0">
          {suggestionsLoading && !suggestion ? (
            <p className="text-[12px] text-grey-400">Checking for a possible policy placement…</p>
          ) : outcome === "implement" ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={OUTCOME_BADGE.implement.className}>{OUTCOME_BADGE.implement.label}</Badge>
                {suggestion?.confidence === "high" ? (
                  <Badge className="bg-teal-100 text-teal-800">High confidence</Badge>
                ) : (
                  // "Lower confidence", not "Needs a look": this row IS an answer-a-policy-question
                  // row, and a badge worded like the separate "Needs Review" OUTCOME made it read as
                  // if it were filed under the wrong chip.
                  <Badge className="bg-grey-100 text-grey-600">Lower confidence</Badge>
                )}
              </div>
              <p className="mt-1.5 text-[13px] text-grey-700">
                Answer <span className="font-semibold">{suggestion?.suggestedItemLabel}</span> in{" "}
                <span className="font-semibold">{suggestion?.suggestedPolicyTitle}</span> with &quot;{suggestion?.suggestedSentence}&quot;
              </p>
              {suggestion?.why && <p className="mt-1 text-[12px] text-grey-400">{suggestion.why}</p>}
              {/* Only high-confidence placements are tickable. A medium/low match still shows its
                  proposal, but writing it is a manual decision — the model itself said it was unsure. */}
              {selectable && (
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-[12px] font-semibold text-grey-700">
                  <input type="checkbox" checked={selected} onChange={onToggleSelected} className="size-3.5 accent-orange-500" />
                  Apply this answer
                </label>
              )}
            </>
          ) : outcome === "catalog-gap" ? (
            <>
              <Badge className={OUTCOME_BADGE["catalog-gap"].className}>{OUTCOME_BADGE["catalog-gap"].label}</Badge>
              <p className="mt-1.5 text-[13px] text-grey-700">{suggestion?.catalogGap}</p>
              <p className="mt-1 text-[12px] text-grey-400">No existing question captures this. Worth adding to the policy catalog.</p>
            </>
          ) : outcome === "already-covered" ? (
            <>
              <Badge className={OUTCOME_BADGE["already-covered"].className}>{OUTCOME_BADGE["already-covered"].label}</Badge>
              <p className="mt-1.5 text-[13px] text-grey-500">Already captured by something this org is migrating. No action needed.</p>
            </>
          ) : outcome === "true-gap" ? (
            <>
              <Badge className={OUTCOME_BADGE["true-gap"].className}>{OUTCOME_BADGE["true-gap"].label}</Badge>
              <p className="mt-1.5 text-[13px] text-grey-500">
                {group.reason ?? "Checked against Details and Policy Builder — no destination found in either."}
              </p>
            </>
          ) : outcome === "needs-review" ? (
            <>
              <Badge className={OUTCOME_BADGE["needs-review"].className}>{OUTCOME_BADGE["needs-review"].label}</Badge>
              <p className="mt-1.5 text-[13px] text-grey-500">Didn&apos;t clearly match a policy, but may still be relevant — worth a manual look.</p>
            </>
          ) : (
            // Loading finished but this item never got a result — a real (rare) possibility, not a
            // rendering bug: an honest "we don't know" state instead of a blank cell.
            <p className="text-[12px] text-grey-400 italic">No suggestion available</p>
          )}
        </div>
      </div>

      {expanded && multiLocation && (
        <div className="px-5 pb-3.5">
          <LocationBreakdown group={{ ...group, locations: group.locations.filter((l) => (l.value ?? "").trim() === variant.value) }} />
        </div>
      )}
    </div>
  );
}

/** Fields per suggestions request. Each request must finish inside APIService's 60s per-attempt
 * timeout, and suggestions cost several LLM calls per field, so the whole org cannot go in one
 * request: an org with 159 free-text fields took ~10 minutes, the request aborted, and the failure
 * was swallowed — the tab showed no categories and no suggested text at all. Chunking keeps every
 * request short AND lets results render as they arrive instead of all at once at the end. */
const SUGGESTION_CHUNK_SIZE = 12;
/** Chunks in flight at once. Enough to keep wall time down, low enough to stay polite to the model. */
const SUGGESTION_CHUNK_CONCURRENCY = 3;

/** Everything one full suggestion load produced, for the automatic save. */
interface SuggestionLoadResult {
  suggestions: CustomizationSuggestion[];
  failedChunks: number;
}

/**
 * Fetch suggestions a chunk at a time, merging each into state as it lands. One failed chunk costs
 * only its own rows — the rest of the page still fills in — which is why this does not reject. It
 * resolves with every judgement loaded and how many chunks failed, so the caller can save the run.
 */
async function loadSuggestionsInChunks(
  orgId: number,
  freeTextGroups: CortexCustomizationGroup[],
  carryingOver: CortexCustomizationGroup[],
  setSuggestions: React.Dispatch<React.SetStateAction<Map<string, CustomizationSuggestion>>>
): Promise<SuggestionLoadResult> {
  const chunks: CortexCustomizationGroup[][] = [];
  for (let i = 0; i < freeTextGroups.length; i += SUGGESTION_CHUNK_SIZE) chunks.push(freeTextGroups.slice(i, i + SUGGESTION_CHUNK_SIZE));

  const loaded: CustomizationSuggestion[] = [];
  let failedChunks = 0;
  let next = 0;
  const worker = async (): Promise<void> => {
    for (let i = next++; i < chunks.length; i = next++) {
      try {
        const sugs = await API.Cortex.policyCustomizationSuggestions(orgId, chunks[i], carryingOver);
        loaded.push(...sugs);
        setSuggestions((prev) => {
          const merged = new Map(prev);
          for (const s of sugs) merged.set(suggestionKey(s.key, s.value), s);
          return merged;
        });
      } catch {
        // Best-effort per chunk: the rest of the page still fills in. Counted so the run isn't saved
        // as if it were complete.
        failedChunks++;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(SUGGESTION_CHUNK_CONCURRENCY, chunks.length) }, worker));
  return { suggestions: loaded, failedChunks };
}

/**
 * Step 3 — review page for content that won't carry over into Cortex V3 (ENG-3133).
 *
 * Ordered by what anyone actually acts on, not by the report's own structure:
 *
 * 1. **Free text not carrying over** — expanded and first. An operator's own written words with
 *    nowhere to go, which is the reason this page exists. Its two no-action siblings (bot-handled
 *    filler, already covered) stay attached to it rather than drifting to the bottom, because they
 *    are the same content triaged out, and splitting them from it hid why they were there.
 * 2. **What's being migrated** — collapsed, low interest here.
 * 3. **Toggles/settings not carrying over**, plus "dropped on purpose" — grouped by key family, each
 *    collapsed. A key customized at many locations is one row with an expandable per-location
 *    breakdown, not one row per location.
 *
 * A placement suggestion (which policy question a free-text field answers, and the answer) loads in
 * the background per field. Most are advisory. The exception is a placement the model itself rated
 * high-confidence: those are pre-ticked and can be written into Policy Builder from here, so this
 * page is no longer read-only. Nothing writes without the operator pressing apply.
 */
export const CustomizedUnmappedTab = forwardRef<MigrationStepHandle, CustomizedUnmappedTabProps>(function CustomizedUnmappedTab(
  { orgId, onApplied, onRunSaved },
  ref
) {
  const [report, setReport] = useState<CortexCustomizationReport>(EMPTY_REPORT);
  const [loading, setLoading] = useState(false);
  const [migratingExpanded, setMigratingExpanded] = useState(false);
  const [botHandledExpanded, setBotHandledExpanded] = useState(false);
  const [alreadyCoveredExpanded, setAlreadyCoveredExpanded] = useState(false);
  const [droppedOnPurposeExpanded, setDroppedOnPurposeExpanded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [suggestions, setSuggestions] = useState<Map<string, CustomizationSuggestion>>(new Map());
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [outcomeFilter, setOutcomeFilter] = useState<Outcome | "all">("all");
  // Pre-ticked as each high-confidence suggestion lands; the operator unticks what they disagree with.
  // Keyed by `suggestionKey`, the same (key, value) identity the suggestions map uses.
  const [deselected, setDeselected] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<PlacementApplyResult | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [runSave, setRunSave] = useState<RunSaveState>({ status: "loading" });
  // Identifies the current load, so a slower earlier load (before "Scan again") never saves over it.
  const loadIdRef = useRef(0);
  const lastJudgedRef = useRef<CustomizationSuggestion[]>([]);

  /** Archive the run to S3 and open or update the CX review ticket. Only this page produces the
   * judgements (in chunks, too slowly for one request), so it saves them itself once they're in. */
  const saveRun = useCallback(
    async (judged: CustomizationSuggestion[], loadId: number): Promise<void> => {
      lastJudgedRef.current = judged;
      setRunSave({ status: "saving" });
      try {
        const result = await API.Cortex.policyUnmigratedReview(orgId, judged);
        if (loadId !== loadIdRef.current) return;
        setRunSave({ status: "saved", result });
        onRunSaved();
      } catch (err) {
        if (loadId !== loadIdRef.current) return;
        setRunSave({ status: "failed", reason: err instanceof Error ? err.message : String(err), retry: "save" });
      }
    },
    [orgId, onRunSaved]
  );

  const reload = useCallback(async () => {
    const loadId = ++loadIdRef.current;
    setLoading(true);
    setRunSave({ status: "loading" });
    setSuggestions(new Map());
    setDeselected(new Set());
    setApplyResult(null);
    setApplyError(null);
    setExpandedCategories(new Set());
    setOutcomeFilter("all");
    try {
      let r: CortexCustomizationReport;
      try {
        r = await API.Cortex.policyCustomizationReport(orgId);
      } catch (err) {
        setRunSave({ status: "failed", reason: "the report failed to load.", retry: "reload" });
        throw err;
      }
      setReport(r);
      // Fires automatically — no button — but doesn't block the report's own render; the free-text
      // section shows its own per-row loading state while this resolves in the background. Once every
      // suggestion is in, the run saves itself. An incomplete load is not saved: it would under-report
      // to CX as if it were this org's real result.
      const freeTextGroups = r.notCarryingOver.filter((g) => g.kind === "free-text");
      const saveWhenComplete = (loadResult: SuggestionLoadResult): void => {
        if (loadId !== loadIdRef.current) return;
        if (r.scanIncomplete) {
          setRunSave({ status: "failed", reason: "part of the legacy text scan failed.", retry: "reload" });
        } else if (loadResult.failedChunks > 0) {
          setRunSave({ status: "failed", reason: "some suggestions failed to load.", retry: "reload" });
        } else {
          void saveRun(loadResult.suggestions, loadId);
        }
      };
      if (freeTextGroups.length > 0) {
        setSuggestionsLoading(true);
        void loadSuggestionsInChunks(orgId, freeTextGroups, r.carryingOver, setSuggestions)
          .finally(() => setSuggestionsLoading(false))
          .then(saveWhenComplete);
      } else {
        saveWhenComplete({ suggestions: [], failedChunks: 0 });
      }
    } finally {
      setLoading(false);
    }
  }, [orgId, saveRun]);

  useEffect(() => {
    reload().catch(() => {
      /* handled in reload */
    });
  }, [reload]);

  const toggleGroups = useMemo(() => report.notCarryingOver.filter((g) => g.kind === "toggle"), [report]);
  const freeTextGroups = useMemo(() => report.notCarryingOver.filter((g) => g.kind === "free-text"), [report]);
  // A toggle that matched no migration branch at all was never a Details or Policy Builder candidate
  // in the first place (an admin/integration flag, most commonly) — split from the ones that DID match
  // a branch but failed this particular attempt, which are still worth reviewing in the main list.
  const mappedToggleGroups = useMemo(() => toggleGroups.filter((g) => g.hasPotentialDestination), [toggleGroups]);
  const droppedOnPurposeGroups = useMemo(() => toggleGroups.filter((g) => !g.hasPotentialDestination), [toggleGroups]);
  const toggleSections = useMemo(() => groupByCategory(mappedToggleGroups), [mappedToggleGroups]);
  // An item starts in the main list and only moves to the bot-handled bucket once its suggestion
  // arrives and says so — nothing is filtered before the classification is known.
  // A field moves to the bot-handled bucket only when EVERY one of its distinct values was triaged
  // as filler — one location's real policy text must not be hidden by its siblings' boilerplate.
  const isFullyBotHandled = useCallback(
    (g: CortexCustomizationGroup): boolean => {
      const variants = variantsOf(g);
      return variants.length > 0 && variants.every((v) => suggestions.get(suggestionKey(g.key, v.value))?.isBotHandledFiller === true);
    },
    [suggestions]
  );
  const botHandledGroups = useMemo(() => freeTextGroups.filter(isFullyBotHandled), [freeTextGroups, isFullyBotHandled]);
  // Same treatment, same rule: content this org is already migrating elsewhere needs no action, so it
  // leaves the review list — but only when EVERY distinct value is covered, so one location's genuinely
  // uncovered text can't be hidden by its siblings. Evaluated after bot-handled so a group that is both
  // lands in exactly one section.
  const isFullyAlreadyCovered = useCallback(
    (g: CortexCustomizationGroup): boolean => {
      const variants = variantsOf(g);
      return variants.length > 0 && variants.every((v) => suggestions.get(suggestionKey(g.key, v.value))?.alreadyCovered === true);
    },
    [suggestions]
  );
  const alreadyCoveredGroups = useMemo(
    () => freeTextGroups.filter((g) => !isFullyBotHandled(g) && isFullyAlreadyCovered(g)),
    [freeTextGroups, isFullyBotHandled, isFullyAlreadyCovered]
  );
  const actionableFreeTextGroups = useMemo(
    () => freeTextGroups.filter((g) => !isFullyBotHandled(g) && !isFullyAlreadyCovered(g)),
    [freeTextGroups, isFullyBotHandled, isFullyAlreadyCovered]
  );
  // An item whose suggestion hasn't loaded yet counts toward the section total but not any specific
  // outcome chip — it doesn't have a badge yet either, so counts grow into place as suggestions
  // resolve rather than starting wrong and correcting.
  // Counts, filtering and rendering are all per VARIANT: a field holding different text at different
  // scopes contributes one row (and one outcome) per distinct value, so a chip's number is exactly
  // how many rows that chip shows. See `visibleFreeTextRows` — these used to disagree.
  // The header and the "All" chip count rows too (ENG-3410): counting fields there disagreed with the
  // chips and the table whenever a field held different text at different scopes.
  const freeTextRowCount = useMemo(() => actionableFreeTextGroups.reduce((n, g) => n + variantsOf(g).length, 0), [actionableFreeTextGroups]);
  const outcomeCounts = useMemo(() => {
    const counts: Record<Outcome, number> = { implement: 0, "catalog-gap": 0, "already-covered": 0, "true-gap": 0, "needs-review": 0 };
    // `already-covered` stays in the record so `outcomeFor` has somewhere to land, but it has no chip:
    // a fully-covered field is in its own section, and a mixed field's covered variant is not a filter
    // anyone needs.
    for (const g of actionableFreeTextGroups) {
      for (const variant of variantsOf(g)) {
        const outcome = outcomeFor(suggestions.get(suggestionKey(g.key, variant.value)));
        if (outcome) counts[outcome]++;
      }
    }
    return counts;
  }, [actionableFreeTextGroups, suggestions]);
  /**
   * The rows to render, already narrowed to the active filter.
   *
   * Filtering and rendering used to be decided in two places that disagreed: the filter kept a GROUP
   * when ANY of its variants matched (`.some`), and the table then rendered ALL of that group's
   * variants. So a field holding different text at different scopes — one value judged `implement`,
   * another `needs-review` — showed up under BOTH chips, with both rows visible under each. The chip
   * counts were already per variant, so they disagreed with what was on screen too.
   *
   * Deciding both here means a row appears under exactly one outcome chip, which is what the counts
   * have always claimed. `totalVariants` stays the field's real variant count, because the "one of N
   * different values" note describes the FIELD and must not shrink just because a filter is active.
   */
  const visibleFreeTextRows = useMemo(
    () =>
      actionableFreeTextGroups
        .map((g) => {
          const all = variantsOf(g);
          const matching =
            outcomeFilter === "all"
              ? all
              : all.filter((variant) => outcomeFor(suggestions.get(suggestionKey(g.key, variant.value))) === outcomeFilter);
          return { group: g, matching, totalVariants: all.length };
        })
        .filter((row) => row.matching.length > 0),
    [actionableFreeTextGroups, suggestions, outcomeFilter]
  );

  // Every high-confidence placement on the page, paired with the group it came from (the server needs
  // the group to recover which scope to write at — a suggestion is keyed by (key, value) only).
  const applicable = useMemo(
    () =>
      actionableFreeTextGroups.flatMap((g) =>
        variantsOf(g)
          .map((variant) => ({ id: suggestionKey(g.key, variant.value), suggestion: suggestions.get(suggestionKey(g.key, variant.value)) }))
          .filter((row) => isAutoApplicable(row.suggestion))
      ),
    [actionableFreeTextGroups, suggestions]
  );
  const selectedPlacements = useMemo(
    () =>
      applicable
        .filter((row) => !deselected.has(row.id))
        .map((row) => ({
          key: row.suggestion!.key,
          value: row.suggestion!.value,
          policyKey: row.suggestion!.suggestedPolicyKey!,
          itemKey: row.suggestion!.suggestedItemKey!,
          answer: row.suggestion!.suggestedSentence!
        })),
    [applicable, deselected]
  );

  const toggleSelected = useCallback((id: string): void => {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Write the ticked placements into Policy Builder. Scope is resolved server-side, which is why the
   * groups travel with the selections. */
  const handleRetryScan = useCallback((): void => {
    reload().catch(() => {
      /* handled in reload */
    });
  }, [reload]);

  const handleApplySelected = useCallback(async (): Promise<void> => {
    setApplying(true);
    setApplyError(null);
    try {
      setApplyResult(await API.Cortex.policyApplySuggestedPlacements(orgId, report.notCarryingOver, selectedPlacements));
      onApplied();
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : "Could not apply these answers.");
    } finally {
      setApplying(false);
    }
  }, [orgId, report.notCarryingOver, selectedPlacements, onApplied]);

  useImperativeHandle(ref, () => ({ reload }), [reload]);

  const handleRetrySave = useCallback((): void => {
    if (runSave.status === "failed" && runSave.retry === "save") {
      void saveRun(lastJudgedRef.current, loadIdRef.current);
      return;
    }
    handleRetryScan();
  }, [runSave, saveRun, handleRetryScan]);

  const toggleCategory = (label: string): void => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="body-sm mx-auto flex max-w-5xl items-center gap-2 p-6 text-grey-400">
        <Spinner /> Loading…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="heading-base mb-1">Unmigrated content</h2>
          <p className="body-sm text-grey-500">
            What won&apos;t carry over into Cortex V3 — especially anything the operator wrote themselves that has no matching policy — so you can
            tell customers what changed.
          </p>
        </div>
        <Button variant={ButtonVariant.GHOST} onClick={() => downloadCsv(report, suggestions, orgId)}>
          Download CSV
        </Button>
      </div>

      <UnmigratedRunCallout state={runSave} onRetry={handleRetrySave} />
      {report.scanIncomplete && <ScanIncompleteNotice onRetry={handleRetryScan} />}

      {/* The automatic half. Only placements the model itself rated high-confidence appear here, and
          each is pre-ticked but individually untickable — the operator confirms, nothing writes on
          its own. Scope (org vs per-location) is resolved server-side from the group. */}
      {applicable.length > 0 && !applyResult && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded border border-teal-200 bg-teal-50 px-4 py-3">
          <p className="body-sm text-teal-900">
            <span className="font-semibold">{selectedPlacements.length}</span> of {applicable.length} high-confidence answer
            {applicable.length === 1 ? "" : "s"} ready to write into Policies. Untick any you disagree with, then apply — these are written
            immediately.
          </p>
          <Button variant={ButtonVariant.PRIMARY} disabled={applying || selectedPlacements.length === 0} onClick={() => void handleApplySelected()}>
            {applying ? "Applying…" : `Apply ${selectedPlacements.length} answer${selectedPlacements.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      )}
      {applyError && <p className="body-sm mb-4 text-red-700">{applyError}</p>}
      {/* Per placement, with its scope spelled out. An answer written only at three locations is
          genuinely absent from the org-level Policies page, so "where" is the part that matters. */}
      {applyResult && (
        <div className="mb-4 rounded border border-teal-200 bg-teal-50 px-4 py-3">
          <p className="body-sm font-semibold text-teal-900">
            Applied {applyResult.appliedPlacementCount} of {applyResult.placements.length} answer
            {applyResult.placements.length === 1 ? "" : "s"} ({applyResult.writeCount} worksheet write
            {applyResult.writeCount === 1 ? "" : "s"}).
          </p>
          <ul className="mt-2 flex flex-col gap-1">
            {applyResult.placements.map((p) => (
              <li key={`${p.key}#${p.itemKey}`} className="body-xs flex flex-wrap items-baseline gap-1.5 text-teal-900">
                <span className={p.applied ? "text-teal-700" : "text-red-700"}>{p.applied ? "✓" : "✕"}</span>
                <span className="font-semibold">{p.itemKey}</span>
                <span className="text-teal-800">= &quot;{p.answer}&quot;</span>
                <span className="text-grey-600">
                  {p.scope === "org"
                    ? "at org level (every location shared this value)"
                    : p.scope === "locations"
                      ? `at ${p.locationIds.length} location${p.locationIds.length === 1 ? "" : "s"} only — not at org level`
                      : "not written"}
                </span>
                {p.error && <span className="text-red-700">{p.error}</span>}
              </li>
            ))}
          </ul>
          {applyResult.placements.some((p) => p.scope === "locations") && (
            <p className="body-xs mt-2 text-grey-600">
              Location-scoped answers only appear when you view those specific locations in Policies, not on the org-level page.
            </p>
          )}
        </div>
      )}

      {/* 1. Free text not carrying over — expanded, highlighted, and FIRST: it is the actual reason this
       * page exists, and it is the only section anyone acts on. Everything below is reference. */}
      <div>
        <div className="mb-2 flex items-baseline gap-2">
          <span className="text-[11px] font-bold tracking-[0.06em] text-red-700 uppercase">Free text not carrying over</span>
          <span className="body-xs text-grey-500">
            {freeTextRowCount} value{freeTextRowCount === 1 ? "" : "s"} the operator wrote themselves — tell the customer what changed
          </span>
        </div>
        {actionableFreeTextGroups.length === 0 ? (
          <p className="body-sm text-grey-400">None — nothing free-typed is stuck without a home for this org.</p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-2">
              {(
                [
                  { value: "all", label: "All", count: freeTextRowCount },
                  { value: "implement", label: OUTCOME_BADGE.implement.label, count: outcomeCounts.implement },
                  { value: "catalog-gap", label: OUTCOME_BADGE["catalog-gap"].label, count: outcomeCounts["catalog-gap"] },
                  { value: "true-gap", label: OUTCOME_BADGE["true-gap"].label, count: outcomeCounts["true-gap"] },
                  { value: "needs-review", label: OUTCOME_BADGE["needs-review"].label, count: outcomeCounts["needs-review"] }
                ] as const
              ).map((opt) => {
                const active = outcomeFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setOutcomeFilter(opt.value)}
                    className={filterClasses(
                      "inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-[12px] font-semibold transition-colors",
                      active ? "border-grey-900 bg-grey-900 text-white" : "border-grey-200 bg-white text-grey-600 hover:bg-grey-50"
                    )}
                  >
                    {opt.label}
                    <span className={active ? "text-grey-300" : "text-grey-400"}>{opt.count}</span>
                  </button>
                );
              })}
            </div>
            <div className="overflow-hidden rounded-[5px] border border-grey-200 bg-white shadow-panel">
              <div className={filterClasses(FREE_TEXT_ROW_GRID, "border-b border-grey-200 bg-grey-50 px-5 py-2")}>
                <span className={FREE_TEXT_HEADER_LABEL}>Legacy field</span>
                <span className={FREE_TEXT_HEADER_LABEL}>Exact value</span>
                <span className={FREE_TEXT_HEADER_LABEL}>Suggested outcome</span>
              </div>
              {visibleFreeTextRows.length === 0 ? (
                <p className="body-sm px-5 py-4 text-grey-400">No items match this filter.</p>
              ) : (
                visibleFreeTextRows.map(({ group: g, matching, totalVariants }) => {
                  const shown = matching.slice(0, MAX_SHOWN_VARIANTS);
                  const hidden = matching.length - shown.length;
                  return (
                    <div key={g.key}>
                      {shown.map((variant) => (
                        <FreeTextOutcomeRow
                          key={`${g.key}#${variant.value}`}
                          group={g}
                          variant={variant}
                          variantCount={totalVariants}
                          suggestion={suggestions.get(suggestionKey(g.key, variant.value))}
                          suggestionsLoading={suggestionsLoading}
                          selectable={isAutoApplicable(suggestions.get(suggestionKey(g.key, variant.value)))}
                          selected={!deselected.has(suggestionKey(g.key, variant.value))}
                          onToggleSelected={() => toggleSelected(suggestionKey(g.key, variant.value))}
                        />
                      ))}
                      {hidden > 0 && (
                        <p className="border-b border-grey-200 px-5 py-2 text-[12px] text-grey-400 last:border-b-0">
                          {hidden} further distinct value{hidden === 1 ? "" : "s"} for {g.label} — per-location data, reviewed as the samples above
                          rather than one by one.
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {/* 1b. Bot-handled filler — legacy text Snowy Voice already covers conversationally (e.g. "I
       * understand how important it is to have your lease agreement on hand."). De-emphasized and
       * collapsed by default, never hidden — a wrong call here should read as "in the wrong bucket,"
       * not "invisible." */}
      {botHandledGroups.length > 0 && (
        <div className="mt-3 overflow-hidden rounded border border-grey-200 bg-white">
          <button
            type="button"
            onClick={() => setBotHandledExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-3 py-2.5 text-left"
          >
            <span className="body-sm-alt text-grey-700">
              Bot-handled — no action needed <span className="body-xs font-normal text-grey-400">({botHandledGroups.length})</span>
            </span>
            <FiChevronDown className={filterClasses("size-4 text-grey-400 transition-transform", botHandledExpanded && "rotate-180")} />
          </button>
          {botHandledExpanded && (
            <div className="border-t border-grey-200">
              <div className={filterClasses(CUSTOMIZATION_ROW_GRID, "border-b border-grey-200 bg-grey-50 px-4 py-2")}>
                <span className={CUSTOMIZATION_HEADER_LABEL}>Legacy field</span>
                <span className={CUSTOMIZATION_HEADER_LABEL}>Value &amp; reason</span>
              </div>
              {botHandledGroups.map((g) => (
                <CustomizationRow key={g.key} group={g} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content this org is already migrating through another field. No action, so it stays out of
       * the review list — but collapsed rather than dropped, for the same reason as bot-handled: a
       * wrong "already covered" call should read as "in the wrong bucket," not "invisible." */}
      {alreadyCoveredGroups.length > 0 && (
        <div className="mt-3 overflow-hidden rounded border border-grey-200 bg-white">
          <button
            type="button"
            onClick={() => setAlreadyCoveredExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-3 py-2.5 text-left"
          >
            <span className="body-sm-alt text-grey-700">
              Already covered — no action needed <span className="body-xs font-normal text-grey-400">({alreadyCoveredGroups.length})</span>
            </span>
            <FiChevronDown className={filterClasses("size-4 text-grey-400 transition-transform", alreadyCoveredExpanded && "rotate-180")} />
          </button>
          {alreadyCoveredExpanded && (
            <div className="border-t border-grey-200">
              <div className={filterClasses(CUSTOMIZATION_ROW_GRID, "border-b border-grey-200 bg-grey-50 px-4 py-2")}>
                <span className={CUSTOMIZATION_HEADER_LABEL}>Legacy field</span>
                <span className={CUSTOMIZATION_HEADER_LABEL}>Value &amp; reason</span>
              </div>
              {alreadyCoveredGroups.map((g) => (
                <CustomizationRow key={g.key} group={g} />
              ))}
            </div>
          )}
        </div>
      )}
      {/* 2. What's being migrated — collapsed by default, low interest on this page. */}
      <div className="mt-8 mb-6 overflow-hidden rounded border border-grey-200 bg-white">
        <button
          type="button"
          onClick={() => setMigratingExpanded((v) => !v)}
          className="flex w-full items-center justify-between px-3 py-2.5 text-left"
        >
          <span className="body-sm-alt text-grey-700">
            What&apos;s being migrated <span className="body-xs font-normal text-grey-400">({report.carryingOver.length})</span>
          </span>
          <FiChevronDown className={filterClasses("size-4 text-grey-400 transition-transform", migratingExpanded && "rotate-180")} />
        </button>
        {migratingExpanded &&
          (report.carryingOver.length === 0 ? (
            <p className="body-sm px-3 pb-3 text-grey-400">No customized settings carry over.</p>
          ) : (
            <div className="border-t border-grey-200">
              <div className={filterClasses(CUSTOMIZATION_ROW_GRID, "border-b border-grey-200 bg-grey-50 px-4 py-2")}>
                <span className={CUSTOMIZATION_HEADER_LABEL}>Legacy field</span>
                <span className={CUSTOMIZATION_HEADER_LABEL}>Value &amp; destination</span>
              </div>
              {report.carryingOver.map((g) => (
                <CustomizationRow key={g.key} group={g} />
              ))}
            </div>
          ))}
      </div>

      {/* 3. Toggles/settings not carrying over — grouped by key family, each family collapsed. Only
       * toggles that matched some migration branch (Details or Policy Builder), even if this specific
       * attempt failed — a truly-unmapped toggle lives in "Dropped on purpose" below instead. */}
      <div className="mb-8">
        <h3 className="body-sm-alt mb-2 text-grey-700">
          Toggles &amp; settings not carrying over <span className="body-xs font-normal text-grey-400">({mappedToggleGroups.length})</span>
        </h3>
        {toggleSections.length === 0 ? (
          <p className="body-sm text-grey-400">None.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {toggleSections.map((sec) => {
              const open = expandedCategories.has(sec.label);
              return (
                <div key={sec.label} className="overflow-hidden rounded border border-grey-200 bg-white">
                  <button
                    type="button"
                    onClick={() => toggleCategory(sec.label)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left"
                  >
                    <span className="body-sm-alt text-grey-700">
                      {sec.label} <span className="body-xs font-normal text-grey-400">({sec.groups.length})</span>
                    </span>
                    <FiChevronDown className={filterClasses("size-4 text-grey-400 transition-transform", open && "rotate-180")} />
                  </button>
                  {open && (
                    <div className="border-t border-grey-200">
                      <div className={filterClasses(CUSTOMIZATION_ROW_GRID, "border-b border-grey-200 bg-grey-50 px-4 py-2")}>
                        <span className={CUSTOMIZATION_HEADER_LABEL}>Legacy field</span>
                        <span className={CUSTOMIZATION_HEADER_LABEL}>Value &amp; reason</span>
                      </div>
                      {sec.groups.map((g) => (
                        <CustomizationRow key={g.key} group={g} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 3b. Dropped on purpose — never a Details or Policy Builder candidate at all (admin/integration
       * flags like a review-widget or live-chat toggle), not a mapped setting that merely failed. De-
       * emphasized and collapsed by default, same as "Bot-handled" below — never hidden outright. */}
      {droppedOnPurposeGroups.length > 0 && (
        <div className="mb-8 overflow-hidden rounded border border-grey-200 bg-white">
          <button
            type="button"
            onClick={() => setDroppedOnPurposeExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-3 py-2.5 text-left"
          >
            <span className="body-sm-alt text-grey-700">
              Dropped on purpose — not a Details or Policy Builder candidate{" "}
              <span className="body-xs font-normal text-grey-400">({droppedOnPurposeGroups.length})</span>
            </span>
            <FiChevronDown className={filterClasses("size-4 text-grey-400 transition-transform", droppedOnPurposeExpanded && "rotate-180")} />
          </button>
          {droppedOnPurposeExpanded && (
            <div className="border-t border-grey-200">
              <div className={filterClasses(CUSTOMIZATION_ROW_GRID, "border-b border-grey-200 bg-grey-50 px-4 py-2")}>
                <span className={CUSTOMIZATION_HEADER_LABEL}>Legacy field</span>
                <span className={CUSTOMIZATION_HEADER_LABEL}>Value &amp; reason</span>
              </div>
              {droppedOnPurposeGroups.map((g) => (
                <CustomizationRow key={g.key} group={g} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
